<template>
  <div class="payment-details">
    <BackHeader is-show-title is-static title="Membership details" />

    <div class="payment-details__content">
      <section class="membership-base-info">
        <h3 class="membership-subheader">
          Your membership level <span class="membership-name">{{ membershipTypeHeader }}</span>
        </h3>
        <p v-if="museumRole" class="membership-price">{{ membershipPriceText }}</p>
      </section>

      <section v-if="museumRole && !isUserMembershipLapsed" class="membership-adjust">
        <p v-if="!subscriptionCancelAt" class="membership-subheader membership-adjust__btn">Adjust your membership</p>
        <p v-else class="membership-cancelled-text">
          Your membership has been cancelled and will end on {{ subscriptionCancelAt }} (EST)
        </p>

        <form
          v-if="!subscriptionCancelAt"
          class="membership-adjust-options-wrapper"
          @submit.prevent="handleSubmitMembershipAdjust"
        >
          <ul class="membership-adjust-options">
            <li v-if="membership.subscription.is_monthly" class="membership-adjust-options__item">
              <label class="membership-adjust-option">
                <input
                  v-model="membershipAdjustSelected"
                  type="radio"
                  :value="membershipAdjustOptions.upgradeToAnnual"
                />
                <span class="membership-adjust-option__icon"></span>
                <span class="membership-adjust-option__name">Upgrade to annual membership</span>
              </label>
              <div class="membership-adjust-option__info">
                <p>Receive a physical museum card</p>
                <p>1 year prepaid - {{ membership.upgrade_to_annual_prices }}</p>
              </div>
            </li>
            <li class="membership-adjust-options__item">
              <label class="membership-adjust-option">
                <input
                  v-model="membershipAdjustSelected"
                  type="radio"
                  :value="membershipAdjustOptions.upgradeToClubMember"
                />
                <span class="membership-adjust-option__icon"></span>
                <span class="membership-adjust-option__name">Inquire about upgrading to Club membership</span>
              </label>
            </li>
            <li class="membership-adjust-options__item">
              <label class="membership-adjust-option">
                <input v-model="membershipAdjustSelected" type="radio" :value="membershipAdjustOptions.cancel" />
                <span class="membership-adjust-option__icon"></span>
                <span class="membership-adjust-option__name">Cancel your membership</span>
              </label>
              <div class="membership-adjust-option__info">
                <p>
                  Please note, by taking this action you will not be charged upon renewal and you can continue using
                  your online account and benefits until your membership expires.
                </p>
              </div>
            </li>
          </ul>

          <div class="membership-update-actions">
            <button
              type="submit"
              class="btn border-btn payment-details-form-button"
              :disabled="!membershipAdjustSelected || isFormRequestSending"
            >
              <AppLoader v-if="isFormRequestSending" :border-width="3" :margin="0" :size="20" />
              <span v-else>Save</span>
            </button>
          </div>
        </form>
      </section>
    </div>

    <AppConfirmationModal
      v-model="isShowConfirmDeleteModal"
      title="Cancel your membership"
      @save="confirmCancellation"
    />

    <AppInfoModal v-model="infoModal.show" title="Adjustment Confirmed" @save="closeInfoModal">
      <div v-html="infoModal.message" />
    </AppInfoModal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import cancelMembershipMutation from '@/graphql/me/membership/CancelMembership.mutation.gql';
import meMembershipQuery from '@/graphql/me/membership/MeMembership.query.gql';
import sendUserMembershipUpgradeRequestMutation from '@/graphql/me/membership/SendUserMembershipUpgradeRequest.mutation.gql';
import upgradeToAnnualMembershipMutation from '@/graphql/me/membership/UpgradeToAnnualMembership.mutation.gql';

import AppConfirmationModal from '@/components/modals/AppConfirmationModal';
import AppInfoModal from '@/components/modals/AppInfoModal';
import BackHeader from '@/components/partials/BackHeader';
import CollapseTransition from '@/components/transitions/CollapseTransition';

export default {
  name: 'MembershipDetails',
  components: {
    AppConfirmationModal,
    AppInfoModal,
    BackHeader,
    CollapseTransition,
  },
  metaInfo: {
    title: 'Membership details',
  },

  data() {
    return {
      infoModal: {
        callback: () => {},
        message: '',
        show: false,
      },
      isFormRequestSending: false,
      isShowConfirmDeleteModal: false,
      membershipAdjustOptions: {
        cancel: 'cancel',
        upgradeToAnnual: 'upgrade_to_annual',
        upgradeToClubMember: 'upgrade_to_club_member',
      },
      membershipAdjustSelected: null,
    };
  },

  computed: {
    ...mapState(['membership', 'museumRole']),
    ...mapGetters(['isUserMembershipLapsed']),
    membershipTypeHeader() {
      return this.membership.type.replace(/member/i, '') + ' Member';
    },
    membershipPriceText() {
      let text = this.membership.subscription.price_for_human;
      if (this.membership.subscription.price_hint) {
        text += ` / ${this.membership.subscription.price_hint}`;
      }

      return text;
    },
    subscriptionCancelAt() {
      return this.membership.subscription.cancel_at_formatted;
    },
  },

  created() {
    this.fetchMembershipSubscription();
  },

  methods: {
    handleSubmitMembershipAdjust() {
      if (!this.membershipAdjustSelected || this.isFormRequestSending) {
        return;
      }

      if (this.membershipAdjustSelected === this.membershipAdjustOptions.upgradeToAnnual) {
        this.upgradeToAnnualMembership();
        return;
      }

      if (this.membershipAdjustSelected === this.membershipAdjustOptions.upgradeToClubMember) {
        this.sendMembershipUpgradeRequest();
        return;
      }

      if (this.membershipAdjustSelected === this.membershipAdjustOptions.cancel) {
        this.isShowConfirmDeleteModal = true;
      }
    },

    async fetchMembershipSubscription() {
      const { data } = await this.$apollo.query({ query: meMembershipQuery });
      this.$store.dispatch('setUserMembership', { ...data.me.membership });
    },

    async upgradeToAnnualMembership() {
      try {
        this.isFormRequestSending = true;
        const { data } = await this.$apollo.mutate({
          mutation: upgradeToAnnualMembershipMutation,
        });

        this.$store.dispatch('setUserMembership', data.upgradeToAnnualMembership.data);

        this.infoModal.message = data.upgradeToAnnualMembership.message;
        this.infoModal.show = true;
        this.membershipAdjustSelected = null;
      } catch (e) {
        this.membershipAdjustSelected = null;
        this.$toast.error(e.graphQLErrors[0].message);
      } finally {
        this.isFormRequestSending = false;
      }
    },

    async sendMembershipUpgradeRequest() {
      try {
        this.isFormRequestSending = true;
        const { data } = await this.$apollo.mutate({
          mutation: sendUserMembershipUpgradeRequestMutation,
        });
        this.infoModal.message = data.sendUserMembershipUpgradeRequest.message;
        this.infoModal.show = true;
        this.membershipAdjustSelected = null;
      } catch (e) {
        this.membershipAdjustSelected = null;
        this.$toast.error(e.graphQLErrors[0].message);
      } finally {
        this.isFormRequestSending = false;
      }
    },

    async closeInfoModal() {
      this.infoModal.show = false;
      this.isFormRequestSending = true;
      await this.infoModal.callback();

      this.isFormRequestSending = false;
      this.infoModal.callback = () => {};
    },

    confirmCancellation() {
      this.isShowConfirmDeleteModal = false;
      this.cancelMembership();
    },
    async cancelMembership() {
      try {
        this.isFormRequestSending = true;
        const { data } = await this.$apollo.mutate({
          mutation: cancelMembershipMutation,
        });
        this.infoModal.message = data.cancelUserMembership.message;
        this.membershipAdjustSelected = null;

        if (data.cancelUserMembership.data.subscription.is_cancelled) {
          this.infoModal.callback = this.logoutAfterCancellation;
        } else {
          this.$store.dispatch('setUserMembership', data.cancelUserMembership.data);
        }

        this.infoModal.show = true;
      } catch (e) {
        this.membershipAdjustSelected = null;
        this.$toast.error(e.graphQLErrors[0].message);
      } finally {
        this.isFormRequestSending = false;
      }
    },
    logoutAfterCancellation() {
      this.$store.dispatch('logout', { notSendRequest: true }).then(() => {
        this.$router.push({ name: 'login' });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.membership-base-info {
  padding-top: 7px;
}

.membership-subheader {
  font-size: 16px;
  letter-spacing: 0.18em;
  color: vars.$c-999999;
  text-transform: uppercase;
}

.membership-cancelled-text {
  margin-bottom: 25px;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.05em;
  color: vars.$c-999999;
}

.membership-name {
  display: block;
  margin-top: 12px;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: vars.$c-dark-gray;
}

.membership-price {
  font-size: 14px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.membership-adjust {
  margin-top: 45px;
  padding-bottom: 10px;

  &__btn {
    margin-bottom: 25px;
    padding-left: 0;
  }

  &-options {
    &-wrapper {
      padding-bottom: 20px;
    }

    list-style-type: none;
    padding-left: 0;

    &__item {
      &:not(:first-child) {
        margin-top: 30px;
      }
    }
  }

  &-option {
    display: flex;
    max-width: max-content;
    margin-bottom: 0;
    padding-right: 10px;

    input {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      overflow: hidden;
      clip: rect(0 0 0 0);
      color: transparent;
    }

    &__icon {
      display: block;
      width: 15px;
      height: 15px;
      margin-top: 4px;
      border: 1px solid vars.$c-dark-gray;
      border-radius: 100%;
    }

    input:checked ~ &__icon {
      background-color: vars.$c-dark-gray;
    }

    &__name {
      flex: 1;
      margin-left: 10px;
      font-size: 16px;
      letter-spacing: 0.18em;
      color: vars.$c-dark-gray;
      text-transform: uppercase;
    }

    &__info {
      margin-top: 7px;
      padding-left: 24px;
      font-size: 15px;
      letter-spacing: 0.05em;
      color: vars.$c-999999;

      p {
        margin-bottom: 7px;
      }
    }
  }
}

.membership-update-actions {
  margin-top: 50px;
  text-align: center;

  @media (min-width: vars.$tablet-break-point) {
    text-align: right;
  }
}
</style>
